import React, { useEffect, useState } from 'react';
import { Typography, Breadcrumb, Card, Table, Button, Space, Tag, Modal, Form, Input, Select, message } from 'antd';

const { Title, Paragraph } = Typography;
const { Option } = Select;

const APIKeysManagement = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const columns = [
    { title: 'Key ID', dataIndex: 'id', key: 'id', width: 180 },
    { title: 'Label', dataIndex: 'label', key: 'label' },
    { title: 'Scope', dataIndex: 'scope', key: 'scope', render: s => <Tag>{s}</Tag>, width: 140 },
    { title: 'Created', dataIndex: 'created_at', key: 'created_at', width: 180 },
    { title: 'Last Used', dataIndex: 'last_used', key: 'last_used', width: 180 },
    { title: 'Status', dataIndex: 'status', key: 'status', render: s => <Tag color={s === 'active' ? 'green' : 'red'}>{s}</Tag>, width: 120 },
    {
      title: 'Actions', key: 'actions', width: 260, render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => onRegenerate(record)}>Regenerate</Button>
          <Button size="small" danger onClick={() => onRevoke(record)}>Revoke</Button>
        </Space>
      )
    }
  ];

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      setRows([
        { id: 'pk_live_ABC123', label: 'Production App', scope: 'full', created_at: '2025-07-01 10:00', last_used: '2025-08-12 12:33', status: 'active' },
        { id: 'pk_test_DEF456', label: 'Testing', scope: 'read', created_at: '2025-06-10 09:15', last_used: '2025-08-10 08:22', status: 'active' },
      ]);
      setLoading(false);
    }, 250);
    return () => clearTimeout(t);
  }, []);

  const onCreate = () => {
    form.resetFields();
    form.setFieldsValue({ scope: 'read' });
    setOpen(true);
  };

  const onRegenerate = (record) => {
    // POST /api/security/api-keys/:id/regenerate
    message.success(`Regenerated key ${record.id}`);
  };

  const onRevoke = (record) => {
    // DELETE /api/security/api-keys/:id
    setRows(prev => prev.map(r => r.id === record.id ? { ...r, status: 'revoked' } : r));
    message.success(`Revoked key ${record.id}`);
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      // POST /api/security/api-keys
      await new Promise(r => setTimeout(r, 350));
      const newId = `pk_${Math.random().toString(36).slice(2, 8)}`;
      setRows(prev => ([{ id: newId, status: 'active', created_at: new Date().toISOString().slice(0, 16).replace('T', ' '), last_used: '-', ...values }, ...prev]));
      setOpen(false);
      message.success('API key created');
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.message || 'Failed to create key');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[{ title: 'Admin' }, { title: 'Security' }, { title: 'API Keys' }]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>API Key Management</Title>
        <Space>
          <Button type="primary" onClick={onCreate}>Create API Key</Button>
        </Space>
      </div>

      <Card>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>Manage API keys for programmatic access. Replace with your security endpoints.</Paragraph>
        <Table rowKey="id" columns={columns} dataSource={rows} loading={loading} pagination={{ pageSize: 10 }} />
      </Card>

      <Modal
        open={open}
        title="Create API Key"
        onCancel={() => setOpen(false)}
        onOk={onSubmit}
        confirmLoading={saving}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Label" name="label" rules={[{ required: true }]}>
            <Input placeholder="e.g., Production App" />
          </Form.Item>
          <Form.Item label="Scope" name="scope" rules={[{ required: true }]}>
            <Select>
              <Option value="read">Read-only</Option>
              <Option value="write">Write</Option>
              <Option value="full">Full Access</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default APIKeysManagement;
